import { useRef } from "react";
import formatGroupedValues from "../utils/formatGroupedValues";
import capitalizeFilterLabel from "../utils/capitalizeFilterLabel";
import FilterField from "../fields/FilterField";
import FilterMultiSelect from "../fields/FilterMultiSelect";
import FilterSelect from "../fields/FilterSelect";
import FilterToggle from "../fields/FilterToggle";
import FilterSortGroup from "../fields/FilterSortGroup";

const TYPE_OPTIONS = ["candidate", "fieldstaff", "outsourcedstaff"];

const HST_OPTIONS = [
  { value: "all", label: "All" },
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

const DELIVERY_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pickup", label: "Pickup" },
  { value: "mail", label: "Mail" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "pending activation", label: "Pending Activation" },
];

const TEMP_SORT_OPTIONS = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "created", label: "Created" },
  { value: "type", label: "Type" },
];

const OFFICE_SORT_OPTIONS = [
  { value: "first_name", label: "First Name" },
  { value: "last_name", label: "Last Name" },
  { value: "created", label: "Created" },
];

function FieldWrapper({ fieldId, label, focusedFieldId, onFocusClear, children }) {
  const ref = useRef(null);
  return (
    <FilterField
      ref={ref}
      id={fieldId}
      label={label}
      focusedFieldId={focusedFieldId}
      onFocusClear={onFocusClear}
    >
      {children}
    </FilterField>
  );
}

export function PersonnelFilterFields({
  selectedTab,
  filters,
  setFilters,
  focusedFieldId,
  onFocusClear,
  formatType,
  divisions = [],
  recruiters = [],
  certifications = [],
  skills = [],
  payers = [],
  officeRoleOptions = [],
}) {
  const patch = (partial) => setFilters((prev) => ({ ...prev, ...partial }));

  if (selectedTab === "OFFICE STAFF") {
    return (
      <>
        <FieldWrapper
          fieldId="roles"
          label="Role"
          focusedFieldId={focusedFieldId}
          onFocusClear={onFocusClear}
        >
          <FilterSelect
            id="roles"
            value={filters.roles?.[0] || "all"}
            onChange={(v) =>
              patch({ roles: v === "all" ? [] : [v] })
            }
            options={officeRoleOptions.map((r) => ({
              value: r.name,
              label: r.name,
            }))}
          />
        </FieldWrapper>

        <FieldWrapper
          fieldId="status"
          label="Status"
          focusedFieldId={focusedFieldId}
          onFocusClear={onFocusClear}
        >
          <FilterSelect
            id="status"
            value={filters.status?.[0] || "all"}
            onChange={(v) =>
              patch({ status: v === "all" ? [] : [v] })
            }
            options={STATUS_OPTIONS.filter((o) => o.value !== "all")}
          />
        </FieldWrapper>

        <FieldWrapper
          fieldId="showDeleted"
          label=""
          focusedFieldId={focusedFieldId}
          onFocusClear={onFocusClear}
        >
          <FilterToggle
            id="showDeleted"
            label="Show Deleted"
            checked={Boolean(filters.showDeleted)}
            onChange={(checked) => patch({ showDeleted: checked })}
          />
        </FieldWrapper>

        <FieldWrapper
          fieldId="sort"
          label=""
          focusedFieldId={focusedFieldId}
          onFocusClear={onFocusClear}
        >
          <FilterSortGroup
            id="office-sort"
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            onSortByChange={(v) => patch({ sortBy: v })}
            onSortOrderChange={(v) => patch({ sortOrder: v })}
            sortOptions={OFFICE_SORT_OPTIONS}
          />
        </FieldWrapper>
      </>
    );
  }

  if (selectedTab !== "TEMP STAFF") return null;

  return (
    <>
      <FieldWrapper fieldId="types" label="Type" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="types"
          options={TYPE_OPTIONS}
          value={filters.types}
          onChange={(types) => patch({ types })}
          placeholder="Select types…"
          formatLabel={formatType}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="divisions" label="Divisions" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="divisions"
          options={divisions.map((d) => d.name)}
          value={filters.divisions}
          onChange={(divisionsVal) => patch({ divisions: divisionsVal })}
          placeholder="Select divisions…"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="recruiters" label="Recruiter" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="recruiters"
          options={recruiters.map((r) => r.name)}
          value={filters.recruiters}
          onChange={(recruitersVal) => patch({ recruiters: recruitersVal })}
          placeholder="Select recruiters…"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="certifications" label="Certifications" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="certifications"
          options={certifications}
          value={filters.certifications}
          onChange={(certificationsVal) => patch({ certifications: certificationsVal })}
          placeholder="Select certifications…"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="skills" label="Skills" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="skills"
          options={skills}
          value={filters.skills}
          onChange={(skillsVal) => patch({ skills: skillsVal })}
          placeholder="Select skills…"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="payers" label="Payer" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterMultiSelect
          id="payers"
          options={payers}
          value={filters.payers}
          onChange={(payersVal) => patch({ payers: payersVal })}
          placeholder="Select payers…"
        />
      </FieldWrapper>

      <FieldWrapper fieldId="hst" label="HST" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="hst"
          value={filters.hst || "all"}
          onChange={(v) => patch({ hst: v })}
          options={HST_OPTIONS.filter((o) => o.value !== "all")}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="deliveryMethod" label="Delivery Method" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="deliveryMethod"
          value={filters.deliveryMethod || "all"}
          onChange={(v) => patch({ deliveryMethod: v })}
          options={DELIVERY_OPTIONS.filter((o) => o.value !== "all")}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="status" label="Status" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSelect
          id="status"
          value={filters.status?.[0] || "all"}
          onChange={(v) => patch({ status: v === "all" ? [] : [v] })}
          options={STATUS_OPTIONS.filter((o) => o.value !== "all")}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="showDeleted" label="" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterToggle
          id="showDeleted"
          label="Show Deleted"
          checked={Boolean(filters.showDeleted)}
          onChange={(checked) => patch({ showDeleted: checked })}
        />
      </FieldWrapper>

      <FieldWrapper fieldId="sort" label="" focusedFieldId={focusedFieldId} onFocusClear={onFocusClear}>
        <FilterSortGroup
          id="temp-sort"
          sortBy={filters.sortBy}
          sortOrder={filters.sortOrder}
          onSortByChange={(v) => patch({ sortBy: v })}
          onSortOrderChange={(v) => patch({ sortOrder: v })}
          sortOptions={TEMP_SORT_OPTIONS}
        />
      </FieldWrapper>
    </>
  );
}

/** Map filter state to chip list for AtlasFilterChipBar (one chip per filter field) */
export function buildPersonnelFilterChips(filters, searchTerm, formatType) {
  const chips = [];

  if (searchTerm) {
    chips.push({
      id: "search",
      fieldId: null,
      key: "search",
      value: null,
      label: `Search: ${searchTerm}`,
    });
  }

  const pushGroup = (fieldId, key, prefix, values, formatLabel = capitalizeFilterLabel) => {
    if (!Array.isArray(values) || values.length === 0) return;
    chips.push({
      id: fieldId,
      fieldId,
      key,
      value: null,
      label: `${prefix}: ${formatGroupedValues(values, formatLabel, 3)}`,
    });
  };

  pushGroup("types", "types", "Type", filters.types, formatType);
  pushGroup("divisions", "divisions", "Division", filters.divisions);
  pushGroup("recruiters", "recruiters", "Recruiter", filters.recruiters);
  pushGroup("certifications", "certifications", "Cert", filters.certifications);
  pushGroup("skills", "skills", "Skill", filters.skills);
  pushGroup("payers", "payers", "Payer", filters.payers);
  pushGroup("roles", "roles", "Role", filters.roles);
  pushGroup("status", "status", "Status", filters.status);

  if (filters.hst && filters.hst !== "all") {
    chips.push({
      id: "hst",
      fieldId: "hst",
      key: "hst",
      value: null,
      label: `HST: ${capitalizeFilterLabel(filters.hst)}`,
    });
  }

  if (filters.deliveryMethod && filters.deliveryMethod !== "all") {
    chips.push({
      id: "deliveryMethod",
      fieldId: "deliveryMethod",
      key: "deliveryMethod",
      value: null,
      label: `Delivery: ${capitalizeFilterLabel(filters.deliveryMethod)}`,
    });
  }

  if (filters.showDeleted) {
    chips.push({
      id: "showDeleted",
      fieldId: "showDeleted",
      key: "showDeleted",
      value: null,
      label: "Showing Deleted",
    });
  }

  return chips;
}

export function countPersonnelActiveFilters(filters, searchTerm, formatType = (t) => t) {
  return buildPersonnelFilterChips(filters, searchTerm, formatType).length;
}

export default PersonnelFilterFields;
